import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  LayoutDashboard,
  Heart,
  FileText,
  User,
  LogOut,
  Home,
  IndianRupee,
  Download,
  ShieldCheck,
  Calendar,
  ExternalLink,
  Menu,
  X,
  CheckCircle2,
  AlertCircle,
  PlusCircle,
  Sparkles,
  CreditCard,
  Building2,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '@/config/api';
import { printOfficialReceipt } from '@/utils/printReceipt';
import DonateModal from '../components/DonateModal';

export const UserPortal = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, logout, updateUser, loading: authLoading } = useAuth();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);

  // Data states
  const [donations, setDonations] = useState([]);
  const [loadingDonations, setLoadingDonations] = useState(true);

  // Profile edit state
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    pan: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Sync tab with URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    setIsSidebarOpen(false);
  };

  // Auth gate
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    } else if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        pan: user.pan || ''
      });
      fetchDonations();
    }
  }, [user, authLoading, navigate]);

  const fetchDonations = async () => {
    if (!user?.id) return;
    setLoadingDonations(true);
    try {
      const response = await axios.get(`${API_URL}/donations/user/${user.id}`);
      setDonations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch donations:', error);
    } finally {
      setLoadingDonations(false);
    }
  };

  const handleDownloadReceipt = async (donationId) => {
    try {
      const res = await axios.get(`${API_URL}/donations/receipt/${donationId}`);
      printOfficialReceipt(res.data);
      toast.success('Opening official 80G tax receipt...');
    } catch (err) {
      toast.error('Failed to load official receipt details.');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateUser({
        name: profileForm.name,
        phone: profileForm.phone,
        pan: profileForm.pan.toUpperCase().trim()
      });
      toast.success('Profile and tax details updated successfully!');
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile details.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Signed out successfully.');
  };

  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white font-body">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-secondary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-300 text-sm font-medium">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Calculated Metrics
  const completedDonations = donations.filter((d) => d.status === 'completed');
  const totalContributed = completedDonations.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const eligible80G = Math.round(totalContributed * 0.5); // 50% tax deduction under Sec 80G
  const livesImpacted = Math.max(1, Math.floor(totalContributed / 500));

  const navMenuItems = [
    { id: 'overview', label: 'Dashboard Overview', icon: LayoutDashboard },
    { id: 'donations', label: 'My Donations', icon: Heart, count: completedDonations.length },
    { id: 'certificates', label: '80G Tax Certificates', icon: ShieldCheck },
    { id: 'profile', label: 'Profile & Tax Settings', icon: User }
  ];

  return (
    <div className="min-h-screen flex bg-slate-50 font-body text-slate-800">
      {/* Mobile Drawer Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* ===================== SIDEBAR ===================== */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-50 w-72 bg-slate-900 text-white flex flex-col justify-between transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        }`}
      >
        <div>
          {/* Logo & Portal Brand */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-xl shadow-md">
                <img src="/logo.jpg" alt="Charitage" className="h-8 w-8 object-contain rounded" />
              </div>
              <div>
                <span className="font-heading font-extrabold text-base tracking-tight text-white block">
                  Charitage Foundation
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-secondary flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Donor Portal
                </span>
              </div>
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Profile Mini Card */}
          <div className="p-5 border-b border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-secondary to-orange-400 flex items-center justify-center font-bold text-white shadow-lg shadow-orange-500/20 text-lg flex-shrink-0">
                {user.name ? user.name.charAt(0).toUpperCase() : 'D'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm text-white truncate leading-tight">
                  {user.name || 'Charitage Donor'}
                </h3>
                <p className="text-xs text-slate-400 truncate mt-0.5">{user.email}</p>
                <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  <span>{user.role === 'admin' ? 'System Admin' : 'Verified Contributor'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="p-4 space-y-1.5">
            <p className="px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
              Navigation
            </p>
            {navMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-secondary text-white shadow-md shadow-secondary/25 font-bold'
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    <span>{item.label}</span>
                  </div>
                  {item.count !== undefined && item.count > 0 && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-300'
                      }`}
                    >
                      {item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Footer Actions */}
        <div className="p-4 border-t border-white/10 space-y-2">
          {user.role === 'admin' && (
            <Link
              to="/admin"
              className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30 transition-colors"
            >
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Admin Control Panel
              </span>
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}

          <Link
            to="/"
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Home className="w-4 h-4 text-slate-400" />
            <span>Return to Main Website</span>
          </Link>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ===================== MAIN CONTENT WRAPPER ===================== */}
      <div className="flex-1 flex flex-col min-w-0 lg:pl-72">
        {/* Top Header Bar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 h-20 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              aria-label="Open navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-slate-900 capitalize leading-tight">
                {navMenuItems.find((i) => i.id === activeTab)?.label || 'Overview'}
              </h1>
              <p className="text-xs text-slate-500 hidden sm:block">
                Charitage Foundation Contributor Account
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowDonateModal(true)}
              className="bg-secondary hover:bg-secondary/90 text-white rounded-full font-bold text-xs sm:text-sm px-4 sm:px-5 py-2 shadow-md shadow-secondary/20 flex items-center gap-1.5 transition-transform hover:scale-105"
            >
              <Heart className="w-4 h-4 fill-white" />
              <span>Donate Again</span>
            </Button>

            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-secondary bg-slate-100 hover:bg-slate-200/80 px-3.5 py-2 rounded-full transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              <span>Website</span>
            </Link>

            <button
              onClick={handleLogout}
              title="Sign Out"
              className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Dynamic Body Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto space-y-8">
          {/* Admin Banner if Admin logged into user portal */}
          {user.role === 'admin' && (
            <div className="bg-gradient-to-r from-blue-900 to-indigo-950 text-white rounded-2xl p-5 shadow-lg border border-blue-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-400/30">
                  <ShieldCheck className="w-6 h-6 text-blue-300" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-white">System Admin Detected</h4>
                  <p className="text-xs text-blue-200 mt-0.5">
                    You have administrative access to manage campaigns, blogs, activities, reports, and all users.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/admin')}
                className="bg-white text-blue-950 hover:bg-blue-50 font-bold text-xs sm:text-sm rounded-xl px-5 py-2.5 shadow-md flex-shrink-0"
              >
                Go to Admin Control Panel →
              </Button>
            </div>
          )}

          {/* ===================== TAB 1: OVERVIEW ===================== */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Welcome Hero Banner */}
              <div className="bg-gradient-to-br from-primary via-slate-900 to-primary/90 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
                <div className="absolute right-0 top-0 w-80 h-80 bg-secondary/15 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10 max-w-2xl">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-secondary border border-white/10 mb-3">
                    <Award className="w-3.5 h-3.5" /> Generous Contributor
                  </span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-black tracking-tight text-white">
                    Welcome back, {user?.name || 'Kind Donor'}!
                  </h2>
                  <p className="text-sm sm:text-base text-slate-200 mt-2 leading-relaxed">
                    Thank you for leading with kindness. Your contributions bring quality education, healthcare, and vital community relief to rural families across India.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button
                      onClick={() => setShowDonateModal(true)}
                      className="bg-secondary hover:bg-secondary/90 text-white rounded-full font-bold text-sm px-6 py-2.5 shadow-lg shadow-secondary/25"
                    >
                      Support a Cause Today
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleTabChange('donations')}
                      className="bg-white/10 hover:bg-white/20 text-white border-white/20 rounded-full font-bold text-sm px-5 py-2.5"
                    >
                      View All Contributions
                    </Button>
                  </div>
                </div>
              </div>

              {/* 4 Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <Card className="rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Total Contributed
                      </span>
                      <div className="p-2.5 rounded-xl bg-orange-50 text-secondary">
                        <IndianRupee className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="text-3xl font-heading font-black text-slate-900">
                      ₹{totalContributed.toLocaleString('en-IN')}
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Lifetime verified donations
                    </p>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Contributions
                      </span>
                      <div className="p-2.5 rounded-xl bg-red-50 text-rose-600">
                        <Heart className="w-5 h-5 fill-rose-600/20" />
                      </div>
                    </div>
                    <div className="text-3xl font-heading font-black text-slate-900">
                      {completedDonations.length}
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">Successful transactions</p>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        80G Tax Benefit
                      </span>
                      <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="text-3xl font-heading font-black text-emerald-600">
                      ₹{eligible80G.toLocaleString('en-IN')}
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">50% claimable deduction</p>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Estimated Impact
                      </span>
                      <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                        <Sparkles className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="text-3xl font-heading font-black text-slate-900">
                      {livesImpacted}+
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">Direct beneficiaries supported</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity Table Preview */}
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between p-6 border-b border-slate-100">
                  <div>
                    <CardTitle className="text-lg font-heading font-bold text-slate-900">
                      Recent Donations
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Your latest philanthropic contributions
                    </p>
                  </div>
                  {completedDonations.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTabChange('donations')}
                      className="text-xs font-bold text-secondary hover:text-secondary/80"
                    >
                      View All ({completedDonations.length}) →
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {loadingDonations ? (
                    <div className="p-8 text-center text-sm text-slate-500">
                      <div className="w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      Loading contributions...
                    </div>
                  ) : completedDonations.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 bg-orange-50 text-secondary rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Heart className="w-8 h-8" />
                      </div>
                      <h4 className="font-bold text-slate-800 text-base">No donations recorded yet</h4>
                      <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1 mb-5">
                        Your direct contributions help build schools, provide medical clinics, and empower women in need.
                      </p>
                      <Button
                        onClick={() => setShowDonateModal(true)}
                        className="bg-secondary text-white hover:bg-secondary/90 font-bold text-xs rounded-full px-5 py-2 shadow-md shadow-secondary/20"
                      >
                        Make Your First Contribution
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {completedDonations.slice(0, 4).map((donation) => (
                        <div
                          key={donation.id}
                          className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors"
                        >
                          <div className="flex items-start sm:items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 flex-shrink-0">
                              <CheckCircle2 className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-heading font-bold text-base text-slate-900">
                                  ₹{Number(donation.amount).toLocaleString('en-IN')}
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                  Completed
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {donation.created_at
                                  ? new Date(donation.created_at).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  : 'Recent'}
                                {donation.razorpay_payment_id && (
                                  <span className="ml-2 font-mono text-[11px] text-slate-400">
                                    • ID: {donation.razorpay_payment_id}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadReceipt(donation.id)}
                            className="rounded-xl border-slate-200 text-xs font-bold hover:border-secondary hover:text-secondary self-start sm:self-center flex items-center gap-1.5"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>80G Receipt</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===================== TAB 2: MY DONATIONS ===================== */}
          {activeTab === 'donations' && (
            <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="p-6 border-b border-slate-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-heading font-extrabold text-slate-900">
                    My Contribution History
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Every donation is 100% tax deductible under Section 80G of the Indian Income Tax Act.
                  </p>
                </div>
                <Button
                  onClick={() => setShowDonateModal(true)}
                  className="bg-secondary text-white hover:bg-secondary/90 rounded-full text-xs font-bold px-4 py-2 self-start sm:self-auto flex items-center gap-1.5 shadow-sm"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>New Donation</span>
                </Button>
              </CardHeader>

              <CardContent className="p-0">
                {loadingDonations ? (
                  <div className="p-12 text-center text-sm text-slate-500">
                    <div className="w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    Fetching donation records...
                  </div>
                ) : donations.length === 0 ? (
                  <div className="p-12 text-center">
                    <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="font-bold text-slate-700">No donations found</p>
                    <p className="text-xs text-slate-500 mt-1 mb-4">
                      When you donate through Charitage, your contributions and tax certificates appear here.
                    </p>
                    <Button
                      onClick={() => setShowDonateModal(true)}
                      className="bg-secondary text-white font-bold text-xs rounded-full px-5 py-2"
                    >
                      Make a Contribution
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="py-3.5 px-6">Date</th>
                          <th className="py-3.5 px-6">Amount</th>
                          <th className="py-3.5 px-6">Payment Reference</th>
                          <th className="py-3.5 px-6">Status</th>
                          <th className="py-3.5 px-6 text-right">Tax Receipt</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {donations.map((donation) => {
                          const isSuccess = donation.status === 'completed';
                          return (
                            <tr key={donation.id} className="hover:bg-slate-50/70 transition-colors">
                              <td className="py-4 px-6 text-slate-700 font-medium whitespace-nowrap">
                                {donation.created_at
                                  ? new Date(donation.created_at).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  : 'Recent'}
                              </td>
                              <td className="py-4 px-6">
                                <span className="font-heading font-black text-slate-900 text-base">
                                  ₹{Number(donation.amount).toLocaleString('en-IN')}
                                </span>
                              </td>
                              <td className="py-4 px-6 font-mono text-xs text-slate-500">
                                {donation.razorpay_payment_id || donation.razorpay_order_id || '—'}
                              </td>
                              <td className="py-4 px-6">
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                                    isSuccess
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-amber-100 text-amber-800'
                                  }`}
                                >
                                  {isSuccess && <CheckCircle2 className="w-3 h-3" />}
                                  {donation.status}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-right">
                                {isSuccess ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadReceipt(donation.id)}
                                    className="rounded-lg border-slate-200 text-xs font-bold hover:border-secondary hover:text-secondary inline-flex items-center gap-1.5"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    <span>Download 80G</span>
                                  </Button>
                                ) : (
                                  <span className="text-xs text-slate-400">Not Available</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===================== TAB 3: 80G TAX CERTIFICATES ===================== */}
          {activeTab === 'certificates' && (
            <div className="space-y-6">
              {/* 80G Tax Exemption Card */}
              <div className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="space-y-2 max-w-xl">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-emerald-200 border border-emerald-400/20">
                      <ShieldCheck className="w-3.5 h-3.5" /> Section 80G(5)(vi) Certified
                    </span>
                    <h3 className="text-2xl font-heading font-black text-white">
                      Tax Exemption Under Section 80G
                    </h3>
                    <p className="text-sm text-emerald-100 leading-relaxed">
                      All donations made to Charitage Foundation are eligible for 50% deduction from taxable income under Section 80G of the Indian Income Tax Act.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 text-center flex-shrink-0 min-w-[200px]">
                    <span className="text-xs uppercase tracking-wider text-emerald-200 font-bold block">
                      Total 80G Claimable
                    </span>
                    <span className="text-3xl font-heading font-black text-white mt-1 block">
                      ₹{eligible80G.toLocaleString('en-IN')}
                    </span>
                    <span className="text-[11px] text-emerald-200 mt-1 block">
                      Across {completedDonations.length} receipts
                    </span>
                  </div>
                </div>
              </div>

              {/* PAN Status Warning if PAN is missing */}
              {!user?.pan && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-bold text-amber-900 text-sm">
                      PAN Card Number Required for Valid 80G Exemption
                    </h4>
                    <p className="text-xs text-amber-800 mt-1">
                      As per Income Tax Department (CBDT) regulations, quoting your PAN is mandatory to claim 80G tax deductions on your ITR filing.
                    </p>
                    <button
                      onClick={() => handleTabChange('profile')}
                      className="mt-2 text-xs font-bold text-amber-900 underline hover:text-amber-700"
                    >
                      Update your PAN Card in Profile Settings →
                    </button>
                  </div>
                </div>
              )}

              {/* Available Tax Receipts */}
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardHeader className="p-6 border-b border-slate-100">
                  <CardTitle className="text-lg font-heading font-bold text-slate-900">
                    Official 80G Receipts for Tax Filing
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Click download to generate official digitally compliant 80G receipts with QR verification.
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  {completedDonations.length === 0 ? (
                    <div className="p-12 text-center text-sm text-slate-500">
                      No completed donations eligible for 80G certificates yet.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {completedDonations.map((donation) => (
                        <div
                          key={donation.id}
                          className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                              <FileText className="w-6 h-6" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-heading font-black text-slate-900 text-lg">
                                  ₹{Number(donation.amount).toLocaleString('en-IN')}
                                </span>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                  50% deduction: ₹{Math.round(donation.amount * 0.5).toLocaleString('en-IN')}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Date:{' '}
                                {new Date(donation.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                                {donation.razorpay_payment_id && (
                                  <span className="font-mono ml-2">
                                    • Ref: {donation.razorpay_payment_id}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          <Button
                            size="sm"
                            onClick={() => handleDownloadReceipt(donation.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold px-4 py-2 flex items-center gap-1.5 shadow-sm"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>Download 80G Receipt</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ===================== TAB 4: PROFILE & TAX SETTINGS ===================== */}
          {activeTab === 'profile' && (
            <Card className="rounded-2xl border-slate-200 shadow-sm max-w-2xl">
              <CardHeader className="p-6 border-b border-slate-100">
                <CardTitle className="text-xl font-heading font-extrabold text-slate-900">
                  Profile & Tax Details
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Keep your donor and PAN details up to date for official 80G certificate issuance.
                </p>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSaveProfile} className="space-y-5">
                  <div>
                    <Label htmlFor="profile-name" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Full Legal Name (For 80G Receipt)
                    </Label>
                    <Input
                      id="profile-name"
                      type="text"
                      required
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="Your full legal name"
                      className="mt-1.5 h-11 rounded-xl border-slate-200"
                    />
                  </div>

                  <div>
                    <Label htmlFor="profile-email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Registered Email Address
                    </Label>
                    <Input
                      id="profile-email"
                      type="email"
                      disabled
                      value={user.email}
                      className="mt-1.5 h-11 rounded-xl bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Email cannot be changed.</p>
                  </div>

                  <div>
                    <Label htmlFor="profile-phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Mobile Number
                    </Label>
                    <Input
                      id="profile-phone"
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="10-digit mobile number"
                      className="mt-1.5 h-11 rounded-xl border-slate-200"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="profile-pan" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        PAN Card Number (10 Digits)
                      </Label>
                      <span className="text-[11px] font-semibold text-emerald-600">
                        Required for 80G Tax Exemption
                      </span>
                    </div>
                    <Input
                      id="profile-pan"
                      type="text"
                      maxLength={10}
                      value={profileForm.pan}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, pan: e.target.value.toUpperCase() })
                      }
                      placeholder="ABCDE1234F"
                      className="mt-1.5 h-11 rounded-xl font-mono tracking-wider border-slate-200 uppercase"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Format: 5 letters, 4 numbers, 1 letter (e.g. ABCDE1234F)
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Updates will be reflected on future 80G tax receipts.
                    </p>
                    <Button
                      type="submit"
                      disabled={savingProfile}
                      className="bg-primary hover:bg-primary/95 text-white font-bold text-sm px-6 py-2.5 rounded-xl shadow-md"
                    >
                      {savingProfile ? 'Saving Changes...' : 'Save Profile'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </main>
      </div>

      {/* Donate Modal Instance */}
      <DonateModal open={showDonateModal} onClose={() => setShowDonateModal(false)} />
    </div>
  );
};

export default UserPortal;
